import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { CpRootNode, CustomPageLayout } from '@se/cp.common';
import { SeSideComponentType } from '@se/platform';
import { ModalWindowSize } from '@se/se.ui.html5';

import { CpFlatNode, CpNodeFilter } from '../cp-node';

export const cpFeatureKey = 'custom-page';

export interface RootState {
    readonly [cpFeatureKey]: CpState;
}

export interface CpDialogState {
    buttonCancel: boolean;
    buttonNo: boolean;
    buttonOk: boolean;
    buttonYes: boolean;
    closable: boolean;
    closeOnHeader: boolean;
    description: string;
    fixedWidthPx?: number;
    header: string;
    noPaddingInContent: boolean;
    isError: boolean;
    isWarning: boolean;
    transparent: boolean;
    visible: boolean;
    windowSize: ModalWindowSize;
    zIndex: number;
}

export const INITIAL_DIALOG_CONFIG = {
    buttonCancel: false,
    buttonNo: false,
    buttonOk: false,
    buttonYes: false,
    closable: true,
    closeOnHeader: true,
    description: '',
    header: '',
    noPaddingInContent: false,
    isError: false,
    isWarning: false,
    transparent: false,
    visible: false,
    windowSize: ModalWindowSize.medium,
    zIndex: 99999,
};

export interface NavButton {
    dataBinding: string;
    label: string;
    visible: boolean;
}

export interface CpNavigatorState {
    /**
     * Pulsanti tabselector implicito.
     */
    buttons: NavButton[];
    /**
     * Filtro per i nodi visibili
     */
    filter: CpNodeFilter;
}

export interface CpPageState {
    idCustomPage: string;
    idCustomPageRoot: string;
    idModelTypes: string[];
    modules: string[];
    leftSliderClosed: boolean;
    rightSliderEnabled: boolean;
    rightSliderClosed: boolean;
    sideComponents: SeSideComponentType[];
}

/**
 * Lista flat dei pannelli.
 */
export interface CpNodeState extends EntityState<CpFlatNode> {
    readonly ids: string[];
    /**
     * Nodo radice della custom page.
     */
    readonly root: CpRootNode | null;
}

export interface CpState {
    /**
     * Configurazione dialog globale.
     */
    readonly dialog: CpDialogState;
    /**
     * Il layout selezionato.
     */
    readonly layoutType: CustomPageLayout;
    /**
     * Lo stato del navigatore.
     */
    readonly navigator: CpNavigatorState;
    /**
     * Stato dei nodi di rollup e navigator.
     */
    readonly nodes: CpNodeState;
    /**
     * Dati globali della pagina aperta.
     */
    readonly page: CpPageState | null;
    /**
     * Nodo selezionato su navigatore e rollup.
     */
    readonly selectedNode: string | null;
}

export const cpNodeCustomSelectors = {
    selectOne(state: CpNodeState, id: string) {
        return state.entities[id];
    },
    selectTreeChildren(state: CpNodeState, parentId: string): string[] {
        const parent = state.entities[parentId];
        if (!parent || !parent.children) {
            return [];
        }

        return parent.children.reduce(
            (accum, child) => accum.concat(cpNodeCustomSelectors.selectTreeChildren(state, child)),
            parent.children,
        );
    },
};

export const cpNodeAdapter = createEntityAdapter<CpFlatNode>();

export const cpInitialState: CpState = {
    dialog: INITIAL_DIALOG_CONFIG,
    layoutType: CustomPageLayout.EXTENSIBLE,
    page: null,
    navigator: {
        buttons: [],
        filter: {
            status: {
                edit: false,
                error: false,
                warning: false,
            },
            text: '',
        },
    },
    nodes: cpNodeAdapter.getInitialState({
        ids: [],
        root: null,
    }),
    selectedNode: null,
};
